import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Car, Package, ShoppingBag, Pill, Clock, CheckCircle,
  MapPin, ArrowRight, LogOut, User, Loader2, DollarSign,
  Play, Check, AlertCircle, AlertTriangle, Navigation, RefreshCw, Star, Route, Map, FileText, TrendingUp, Users, MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import api, { jobAPI, driverAPI, locationAPI, ratingAPI } from '../lib/api';
import { toast } from 'sonner';
import RatingDialog from '../components/RatingDialog';
import DisputeDialog from '../components/DisputeDialog';
import DriverRoutes from '../components/DriverRoutes';
import NotificationBell from '../components/NotificationBell';
import EnablePushButton from '../components/EnablePushButton';
import ChatButton from '../components/ChatButton';
import JobFullDetails from '../components/JobFullDetails';
import DriverAssistantButton from '../components/DriverAssistantButton';
import AIConciergeLauncher from '../components/AIConciergeLauncher';
import InteractiveMap from '../components/InteractiveMap';
import PayoutReport from '../components/PayoutReport';
import StoriesBar from '../components/StoriesBar';
import CommunityFeed from './CommunityFeed';
import People from './People';
import useOnlineStatus from '../hooks/useOnlineStatus';
import { devError } from '../lib/devLog';

const POLL_INTERVAL = 5000; // 5 seconds
const LOCATION_UPDATE_INTERVAL = 15000; // 15 seconds
const DRIVER_CUT_PERCENTAGE = 0.80; // Driver keeps 80%
const PLATFORM_FEE_PERCENTAGE = 0.20; // Platform keeps 20%

// Helper component to display earnings breakdown
const EarningsBreakdown = ({ price, showDetails = true }) => {
  const driverCut = price * DRIVER_CUT_PERCENTAGE;
  const platformFee = price * PLATFORM_FEE_PERCENTAGE;
  
  return (
    <div className="text-right">
      <p className="text-2xl font-bold text-green-600" data-testid="driver-earnings">
        ${driverCut.toFixed(2)}
      </p>
      <p className="text-xs text-zinc-500">You earn</p>
      {showDetails && (
        <div className="mt-1 text-xs">
          <span className="text-zinc-400">Total: ${price.toFixed(2)}</span>
          <span className="text-zinc-300 mx-1">|</span>
          <span className="text-zinc-400">Fee: ${platformFee.toFixed(2)}</span>
        </div>
      )}
    </div>
  );
};

const DriverDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  useOnlineStatus();
  const [availableJobs, setAvailableJobs] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [earnings, setEarnings] = useState({ total_earnings: 0, completed_jobs: 0 });
  const [isAvailable, setIsAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [locationSharing, setLocationSharing] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  // Kept in a ref so the setInterval below always sees the latest value
  // (React state-in-closure would otherwise freeze at the first fix).
  const currentLocationRef = useRef(null);
  const lastSentAtRef = useRef(0);
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [jobToRate, setJobToRate] = useState(null);
  const [showDisputeDialog, setShowDisputeDialog] = useState(false);
  const [jobToDispute, setJobToDispute] = useState(null);
  const [ratedJobs, setRatedJobs] = useState(new Set());
  // Driver approval status
  const [driverStatus, setDriverStatus] = useState({ driver_status: 'approved', rejection_reason: null });
  
  const lastPollRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const locationIntervalRef = useRef(null);
  const watchIdRef = useRef(null);

  // Check if driver is approved (can perform actions)
  const isApproved = driverStatus.driver_status === 'approved';
  const isPending = driverStatus.driver_status === 'pending';
  const isRejected = driverStatus.driver_status === 'rejected';

  useEffect(() => {
    fetchData();
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (locationIntervalRef.current) clearInterval(locationIntervalRef.current);
      if (watchIdRef.current && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  // Set up polling
  useEffect(() => {
    if (!loading) {
      pollIntervalRef.current = setInterval(pollForUpdates, POLL_INTERVAL);
      return () => {
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      };
    }
  }, [loading]);

  // Handle location sharing for active jobs AND idle tracking.
  // 'assigned' is included so drivers who were admin-assigned (but haven't
  // tapped Accept yet) still stream GPS — otherwise the map shows a stuck
  // marker at the pickup location.
  useEffect(() => {
    const activeJob = myJobs.find(j => ['assigned', 'accepted', 'in_progress'].includes(j.status));
    
    if (activeJob && !locationSharing) {
      startLocationSharing(activeJob.id);
    } else if (!activeJob && locationSharing) {
      stopLocationSharing();
    }
    
    // Idle location tracking when available but no active job
    if (isAvailable && !activeJob && navigator.geolocation) {
      const sendIdleLocation = () => {
        navigator.geolocation.getCurrentPosition(
          async (pos) => {
            try {
              await api.post('/drivers/location/idle', {
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
              });
            } catch (e) {
              devError('Idle location update failed:', e);
            }
          },
          (geoErr) => { devError('Geolocation error:', geoErr); },
          { enableHighAccuracy: true, maximumAge: 30000 }
        );
      };
      sendIdleLocation();
      const idleInterval = setInterval(sendIdleLocation, 30000);
      return () => clearInterval(idleInterval);
    }
  }, [myJobs, locationSharing, isAvailable]);

  const startLocationSharing = (jobId) => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setLocationSharing(true);
    
    // Get initial position
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        setCurrentLocation(loc);
        currentLocationRef.current = loc;
        sendLocationUpdate(jobId, loc);
        lastSentAtRef.current = Date.now();
      },
      (error) => {
        devError('Error getting location:', error);
        toast.error('Could not get your location. Please enable location services.');
      },
      { enableHighAccuracy: true }
    );

    // Watch position changes. Send an update as soon as the browser hands us
    // a new fix (throttled to LOCATION_UPDATE_INTERVAL) so the customer's
    // tracking map animates continuously instead of only twice a minute.
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const loc = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        setCurrentLocation(loc);
        currentLocationRef.current = loc;
        const now = Date.now();
        if (now - lastSentAtRef.current >= LOCATION_UPDATE_INTERVAL) {
          sendLocationUpdate(jobId, loc);
          lastSentAtRef.current = now;
        }
      },
      (error) => devError('Location watch error:', error),
      { enableHighAccuracy: true, maximumAge: 5000 }
    );

    // Safety-net interval — if the browser stops firing watchPosition
    // (e.g. background tab), still push the last-known fix so the map
    // marker doesn't go stale.
    locationIntervalRef.current = setInterval(() => {
      const activeJob = myJobs.find(j => ['assigned', 'accepted', 'in_progress'].includes(j.status));
      const loc = currentLocationRef.current;
      if (activeJob && loc) {
        sendLocationUpdate(activeJob.id, loc);
        lastSentAtRef.current = Date.now();
      }
    }, LOCATION_UPDATE_INTERVAL);
  };

  const stopLocationSharing = () => {
    setLocationSharing(false);
    if (watchIdRef.current && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (locationIntervalRef.current) {
      clearInterval(locationIntervalRef.current);
      locationIntervalRef.current = null;
    }
  };

  const sendLocationUpdate = async (jobId, location) => {
    try {
      await locationAPI.update({
        job_id: jobId,
        latitude: location.latitude,
        longitude: location.longitude,
      });
    } catch (error) {
      devError('Failed to update location:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [availableRes, myJobsRes, earningsRes, statusRes] = await Promise.all([
        jobAPI.getAvailable(),
        jobAPI.getMyJobs(),
        driverAPI.getEarnings(),
        driverAPI.getMyStatus(),
      ]);
      setAvailableJobs(availableRes.data);
      setMyJobs(myJobsRes.data);
      setEarnings(earningsRes.data);
      setDriverStatus(statusRes.data);
      lastPollRef.current = new Date().toISOString();
      
      // Get driver's availability from stored user
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        setIsAvailable(userData.is_available !== false);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const pollForUpdates = useCallback(async () => {
    try {
      const [availableRes, myJobsRes] = await Promise.all([
        jobAPI.getAvailable(),
        jobAPI.getMyJobs(),
      ]);
      setAvailableJobs(availableRes.data);
      setMyJobs(myJobsRes.data);
    } catch (error) {
      devError('Polling error:', error);
    }
  }, []);

  const handleToggleAvailability = async () => {
    if (!isApproved) {
      toast.error('Your account must be approved to go online');
      return;
    }
    try {
      const response = await driverAPI.toggleAvailability();
      setIsAvailable(response.data.is_available);
      toast.success(response.data.is_available ? 'You are now available' : 'You are now offline');
    } catch (error) {
      toast.error('Failed to update availability');
    }
  };

  const handleAcceptJob = async (jobId) => {
    if (!isApproved) {
      toast.error('Your account must be approved to accept jobs');
      return;
    }
    setActionLoading(jobId);
    try {
      await jobAPI.accept(jobId);
      toast.success('Job accepted! Customer has been notified.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept job');
    } finally {
      setActionLoading(null);
    }
  };

  const handleStartJob = async (jobId) => {
    setActionLoading(jobId);
    try {
      await jobAPI.start(jobId);
      toast.success('Job started! Customer has been notified.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start job');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCompleteJob = async (jobId) => {
    setActionLoading(jobId);
    try {
      await jobAPI.complete(jobId);
      toast.success('Job completed! Earnings updated.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete job');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLogout = () => {
    stopLocationSharing();
    logout();
    navigate('/');
  };

  const handleOpenRating = (job) => {
    setJobToRate(job);
    setShowRatingDialog(true);
  };

  const handleRatingSubmitted = (jobId) => {
    setRatedJobs(prev => new Set([...prev, jobId]));
  };

  // Check which jobs have been rated
  const checkRatedJobs = async (completedJobsList) => {
    try {
      const rated = new Set();
      for (const job of completedJobsList) {
        const response = await ratingAPI.getJobRatings(job.id).catch(() => ({ data: { ratings: [] } }));
        const driverRating = response.data?.ratings?.find(r => r.rater_id === user?.id);
        if (driverRating) {
          rated.add(job.id);
        }
      }
      setRatedJobs(rated);
    } catch (error) {
      devError('Error checking rated jobs:', error);
    }
  };

  // Check rated jobs after jobs are loaded
  useEffect(() => {
    const completedJobsList = myJobs.filter(j => j.status === 'completed');
    if (completedJobsList.length > 0 && user) {
      checkRatedJobs(completedJobsList);
    }
  }, [myJobs, user]);

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      assigned: 'bg-blue-100 text-blue-800',
      accepted: 'bg-indigo-100 text-indigo-800',
      in_progress: 'bg-purple-100 text-purple-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getJobIcon = (jobType, category) => {
    if (jobType === 'ride') return Car;
    const icons = {
      food: ShoppingBag,
      grocery: ShoppingBag,
      pharmacy: Pill,
      package: Package,
    };
    return icons[category] || Package;
  };

  const activeJobs = myJobs.filter(j => ['assigned', 'accepted', 'in_progress'].includes(j.status));
  const completedJobs = myJobs.filter(j => j.status === 'completed');
  const pendingJobs = availableJobs.filter(j => j.status === 'pending');

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
              <Car className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg sm:text-xl tracking-tight text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>LocalMove</span>
            <span className="text-xs bg-zinc-100 px-2 py-1 rounded-full text-zinc-600 ml-1 hidden sm:inline">Driver</span>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Location Sharing Indicator */}
            {locationSharing && (
              <div className="flex items-center gap-1 text-green-600 text-sm">
                <Navigation className="w-4 h-4 animate-pulse" />
                <span className="hidden sm:inline">Sharing location</span>
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm text-zinc-600">{isAvailable ? 'On' : 'Off'}</span>
              <Switch
                checked={isAvailable && isApproved}
                onCheckedChange={handleToggleAvailability}
                disabled={!isApproved}
                data-testid="availability-switch"
              />
            </div>
            <button 
              onClick={fetchData}
              className="text-zinc-500 hover:text-zinc-700 transition-colors hidden sm:block"
              title="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <ChatButton />
            <NotificationBell />
            <EnablePushButton compact />
            <span className="font-medium text-zinc-600 hidden md:block">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="driver-logout-btn">
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Driver Approval Status Banner */}
      {!isApproved && (
        <div className={`border-b ${isPending ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`}>
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center gap-3">
              {isPending ? (
                <>
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-yellow-800" data-testid="pending-status-title">Application Under Review</h3>
                    <p className="text-sm text-yellow-700">
                      Your driver application is being reviewed. You'll be notified once it's approved. 
                      Until then, you cannot accept jobs or toggle availability.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-red-800" data-testid="rejected-status-title">Application Not Approved</h3>
                    <p className="text-sm text-red-700">
                      {driverStatus.rejection_reason 
                        ? `Reason: ${driverStatus.rejection_reason}` 
                        : 'Your application was not approved. Please contact support for more information.'}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <main className="container mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="bg-white border border-zinc-200 rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Total Earnings</p>
                <p className="text-2xl font-bold text-zinc-900" data-testid="total-earnings">
                  ${earnings.total_earnings.toFixed(2)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-zinc-200 rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Completed Jobs</p>
                <p className="text-2xl font-bold text-zinc-900" data-testid="completed-jobs-count">
                  {earnings.completed_jobs}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-white border border-zinc-200 rounded-xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Active Jobs</p>
                <p className="text-2xl font-bold text-zinc-900" data-testid="active-jobs-count">
                  {activeJobs.length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Commission Info Banner */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-4 text-white mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-6 h-6" />
              <div>
                <p className="font-semibold">You keep 80% of every fare</p>
                <p className="text-sm text-green-100">Platform fee: 20% • No hidden charges • Keep 100% of tips</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <div className="text-center">
                <p className="text-2xl font-bold">80%</p>
                <p className="text-green-100">Your Cut</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">20%</p>
                <p className="text-green-100">Platform</p>
              </div>
            </div>
          </div>
        </div>

        {/* Jobs Tabs */}
        <Tabs defaultValue="active" className="space-y-6">
          {/* Scrollable tab row — h-auto so labels aren't clipped, justify-start so
              Available/Active are visible without scrolling on narrow screens. */}
          <div className="relative">
            <TabsList
              className="bg-white border border-zinc-200 flex flex-nowrap justify-start overflow-x-auto w-full h-auto p-1 gap-1 scrollbar-thin scroll-smooth"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <TabsTrigger
                value="active"
                data-testid="active-tab"
                className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap data-[state=active]:bg-orange-500 data-[state=active]:text-white"
              >
                Active ({activeJobs.length})
              </TabsTrigger>
              <TabsTrigger
                value="available"
                data-testid="available-tab"
                className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap data-[state=active]:bg-orange-500 data-[state=active]:text-white"
              >
                Available ({pendingJobs.length})
              </TabsTrigger>
              <TabsTrigger
                value="completed"
                data-testid="completed-tab"
                className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap"
              >
                Completed ({completedJobs.length})
              </TabsTrigger>
              <TabsTrigger
                value="routes"
                data-testid="routes-tab"
                className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap"
              >
                <Route className="w-4 h-4 mr-1.5" />
                Routes
              </TabsTrigger>
              <TabsTrigger
                value="map"
                data-testid="map-tab"
                className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap"
              >
                <Map className="w-4 h-4 mr-1.5" />
                Map
              </TabsTrigger>
              <TabsTrigger
                value="payouts"
                data-testid="payouts-tab"
                className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap"
              >
                <FileText className="w-4 h-4 mr-1.5" />
                Payouts
              </TabsTrigger>
              <TabsTrigger
                value="community"
                data-testid="driver-community-tab"
                className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap"
              >
                <MessageSquare className="w-4 h-4 mr-1.5" />
                Feed
              </TabsTrigger>
              <TabsTrigger
                value="people"
                data-testid="driver-people-tab"
                className="shrink-0 px-3 py-2 min-h-[40px] whitespace-nowrap"
              >
                <Users className="w-4 h-4 mr-1.5" />
                People
              </TabsTrigger>
            </TabsList>
            {/* Right-edge fade to hint "more tabs, scroll →" — hides on desktop */}
            <div
              className="pointer-events-none absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent md:hidden"
              aria-hidden="true"
            />
          </div>

          {/* Available Jobs */}
          <TabsContent value="available" className="space-y-4">
            {!isApproved ? (
              <div className={`text-center py-12 rounded-xl border ${isPending ? 'bg-yellow-50 border-yellow-200' : 'bg-red-50 border-red-200'}`}>
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isPending ? 'bg-yellow-100' : 'bg-red-100'}`}>
                  {isPending ? (
                    <Clock className={`w-8 h-8 text-yellow-500`} />
                  ) : (
                    <AlertCircle className={`w-8 h-8 text-red-500`} />
                  )}
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${isPending ? 'text-yellow-800' : 'text-red-800'}`}>
                  {isPending ? 'Approval Pending' : 'Account Not Approved'}
                </h3>
                <p className={`${isPending ? 'text-yellow-700' : 'text-red-700'} max-w-md mx-auto`}>
                  {isPending 
                    ? 'Your application is being reviewed by our team. You\'ll be able to accept jobs once approved.'
                    : 'Your driver application was not approved. Please contact support for more information.'}
                </p>
              </div>
            ) : pendingJobs.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-zinc-400" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 mb-2">No available jobs</h3>
                <p className="text-zinc-600">Check back soon for new requests!</p>
              </div>
            ) : (
              pendingJobs.map((job) => {
                const Icon = getJobIcon(job.job_type, job.delivery_category);
                const isAssignedToMe = job.status === 'assigned' && job.driver_id === user?.id;
                return (
                  <div
                    key={job.id}
                    className={`bg-white border rounded-xl p-5 fade-in ${isAssignedToMe ? 'border-orange-300 bg-orange-50/30' : 'border-zinc-200'}`}
                    data-testid={`available-job-${job.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                          <Icon className="w-6 h-6 text-orange-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-zinc-900 capitalize">
                              {job.job_type === 'ride' ? 'Standard Ride' : `${job.delivery_category} Delivery`}
                            </h3>
                            {isAssignedToMe && (
                              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                                Assigned to you
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-zinc-600 mt-1">Customer: {job.user_name}</p>
                          <div className="flex items-center gap-2 text-sm text-zinc-500 mt-2">
                            <MapPin className="w-4 h-4" />
                            <span className="truncate max-w-[200px]">{job.pickup_address}</span>
                            <ArrowRight className="w-4 h-4" />
                            <span className="truncate max-w-[200px]">{job.dropoff_address}</span>
                          </div>
                          {job.notes && (
                            <p className="text-sm text-zinc-500 mt-2 italic">"{job.notes}"</p>
                          )}
                        </div>
                      </div>
                      <EarningsBreakdown price={job.price} />
                    </div>
                    
                    {/* Zone & Pricing Details */}
                    {job.zone_name && (
                      <div className="mt-3 flex items-center gap-4 text-xs">
                        <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                          {job.zone_name}
                        </span>
                        {job.base_price && (
                          <span className="text-zinc-500">
                            Base: ${job.base_price.toFixed(2)} + Zone: ${job.zone_addon?.toFixed(2) || '0.00'}
                          </span>
                        )}
                      </div>
                    )}

                    <JobFullDetails job={job} variant="driver" />

                    <div className="mt-4 pt-4 border-t border-zinc-100">
                      <Button
                        onClick={() => handleAcceptJob(job.id)}
                        className="bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={actionLoading === job.id || !isApproved}
                        data-testid={`accept-job-${job.id}`}
                      >
                        {actionLoading === job.id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4 mr-2" />
                        )}
                        {isApproved ? 'Accept Job' : 'Approval Required'}
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </TabsContent>

          {/* Active Jobs */}
          <TabsContent value="active" className="space-y-4">
            {activeJobs.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-zinc-400" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 mb-2">No active jobs</h3>
                <p className="text-zinc-600">Accept a job to get started!</p>
              </div>
            ) : (
              activeJobs.map((job) => {
                const Icon = getJobIcon(job.job_type, job.delivery_category);
                return (
                  <div
                    key={job.id}
                    className="bg-white border border-zinc-200 rounded-xl p-5 fade-in"
                    data-testid={`active-job-${job.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                          <Icon className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-zinc-900 capitalize">
                            {job.job_type === 'ride' ? 'Standard Ride' : `${job.delivery_category} Delivery`}
                          </h3>
                          <p className="text-sm text-zinc-600 mt-1">Customer: {job.user_name}</p>
                          <div className="flex items-center gap-2 text-sm text-zinc-500 mt-2">
                            <MapPin className="w-4 h-4" />
                            <span className="truncate max-w-[200px]">{job.pickup_address}</span>
                            <ArrowRight className="w-4 h-4" />
                            <span className="truncate max-w-[200px]">{job.dropoff_address}</span>
                          </div>
                          
                          {/* Location sharing status */}
                          {locationSharing && (
                            <div className="flex items-center gap-2 text-sm text-green-600 mt-2">
                              <Navigation className="w-4 h-4 animate-pulse" />
                              <span>Location being shared with customer</span>
                            </div>
                          )}
                          
                          {/* Zone info */}
                          {job.zone_name && (
                            <div className="mt-2">
                              <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
                                {job.zone_name}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <EarningsBreakdown price={job.price} />
                    </div>

                    <JobFullDetails job={job} variant="driver" />
                    
                    <div className="mt-4 pt-4 border-t border-zinc-100 flex gap-3 flex-wrap">
                      <DriverAssistantButton jobId={job.id} />
                      {job.status === 'assigned' && (
                        <Button
                          onClick={() => handleAcceptJob(job.id)}
                          className="bg-orange-500 hover:bg-orange-600 text-white"
                          disabled={actionLoading === job.id}
                          data-testid={`accept-assigned-job-${job.id}`}
                        >
                          {actionLoading === job.id ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-2" />
                          )}
                          Accept This Job
                        </Button>
                      )}
                      {job.status === 'accepted' && (
                        <Button
                          onClick={() => handleStartJob(job.id)}
                          className="bg-purple-600 hover:bg-purple-700 text-white"
                          disabled={actionLoading === job.id}
                          data-testid={`start-job-${job.id}`}
                        >
                          {actionLoading === job.id ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Play className="w-4 h-4 mr-2" />
                          )}
                          Start Job
                        </Button>
                      )}
                      {job.status === 'in_progress' && (
                        <Button
                          onClick={() => handleCompleteJob(job.id)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          disabled={actionLoading === job.id}
                          data-testid={`complete-job-${job.id}`}
                        >
                          {actionLoading === job.id ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <CheckCircle className="w-4 h-4 mr-2" />
                          )}
                          Complete Job
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </TabsContent>

          {/* Completed Jobs */}
          <TabsContent value="completed" className="space-y-4">
            {completedJobs.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl border border-zinc-200">
                <div className="w-16 h-16 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-zinc-400" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 mb-2">No completed jobs yet</h3>
                <p className="text-zinc-600">Complete your first job to see it here!</p>
              </div>
            ) : (
              completedJobs.map((job) => {
                const Icon = getJobIcon(job.job_type, job.delivery_category);
                const canRate = !ratedJobs.has(job.id);
                const hasRated = ratedJobs.has(job.id);
                return (
                  <div
                    key={job.id}
                    className="bg-white border border-zinc-200 rounded-xl p-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <Icon className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h3 className="font-medium text-zinc-900 capitalize">
                            {job.job_type === 'ride' ? 'Standard Ride' : `${job.delivery_category} Delivery`}
                          </h3>
                          <p className="text-sm text-zinc-500">
                            {new Date(job.created_at).toLocaleDateString()} • Customer: {job.user_name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Completed
                        </span>
                        <div className="text-right">
                          <p className="font-bold text-green-600">+${(job.price * DRIVER_CUT_PERCENTAGE).toFixed(2)}</p>
                          <p className="text-xs text-zinc-400">earned</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Rating Section */}
                    <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center gap-3 flex-wrap">
                      {canRate ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenRating(job)}
                          className="text-orange-600 border-orange-200 hover:bg-orange-50"
                          data-testid={`rate-customer-${job.id}`}
                        >
                          <Star className="w-4 h-4 mr-2" />
                          Rate Customer
                        </Button>
                      ) : hasRated ? (
                        <div className="flex items-center gap-2 text-sm text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span>You rated this customer</span>
                        </div>
                      ) : null}

                      {(() => {
                        const completedAt = new Date(job.updated_at || job.created_at);
                        const daysSince = (Date.now() - completedAt.getTime()) / (1000 * 60 * 60 * 24);
                        if (daysSince > 7) return null;
                        return (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setJobToDispute(job); setShowDisputeDialog(true); }}
                            className="text-zinc-500 hover:text-red-600"
                            data-testid={`dispute-job-${job.id}`}
                          >
                            <AlertTriangle className="w-4 h-4 mr-1" />
                            Report Issue
                          </Button>
                        );
                      })()}
                    </div>
                  </div>
                );
              })
            )}
          </TabsContent>

          {/* My Routes Tab */}
          <TabsContent value="routes">
            <DriverRoutes />
          </TabsContent>

          {/* Map Tab */}
          <TabsContent value="map">
            <div className="bg-white border border-zinc-200 rounded-xl p-5">
              <h3 className="font-semibold text-zinc-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Service Area Map
              </h3>
              <InteractiveMap height="500px" showJobs={true} showRoutes={true} />
            </div>
          </TabsContent>

          {/* Payouts Tab */}
          <TabsContent value="payouts">
            <PayoutReport />
          </TabsContent>

          {/* Community Feed Tab */}
          <TabsContent value="community">
            <CommunityFeed embedded />
          </TabsContent>

          {/* People Tab */}
          <TabsContent value="people">
            <People embedded />
          </TabsContent>
        </Tabs>
      </main>

      {/* Rating Dialog for Drivers to rate Users */}
      <RatingDialog
        open={showRatingDialog}
        onOpenChange={setShowRatingDialog}
        job={jobToRate}
        onRatingSubmitted={handleRatingSubmitted}
      />

      {/* Dispute Dialog */}
      <DisputeDialog
        open={showDisputeDialog}
        onOpenChange={setShowDisputeDialog}
        job={jobToDispute}
        userRole="driver"
      />
      <AIConciergeLauncher />
    </div>
  );
};

export default DriverDashboard;
